import { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Stack,
  Tooltip,
  Chip,
  CardActionArea,
  Avatar,
  Divider,
  DialogActions,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Container,
  Paper,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  CalendarMonth as CalendarIcon,
  Image as ImageIcon,
  Article as ArticleIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Share as ShareIcon,
  LinkedIn as LinkedInIcon,
  Check as CheckIcon,
  Add as AddIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import postService from '../services/postService';
import AnimatedLogo from '../components/common/AnimatedLogo';
import { useAuth } from '../context/AuthContext';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import LinkedInShareButton from '../components/social/LinkedInShareButton';
import { Helmet } from 'react-helmet-async';

// Utility functions
const formatPostDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const fixSpecialCharacters = (content) => {
  if (!content) return '';
  
  // Replace question marks that are likely arrows
  let fixed = content.replace(/\n\? ([A-Z])/g, '\n→ $1');
  fixed = fixed.replace(/\n\? ([a-z])/g, '\n↳ $1');
  
  return fixed;
};

// Post Card Component
const PostCard = ({ post, onSelect, onMenuOpen, onCopy, onCalendar, onLinkedIn, copySuccess, onSchedule }) => (
  <Card 
    sx={{ 
      mb: 2,
      transition: 'transform 0.2s',
      border: '1px solid rgba(0, 0, 0, 0.08)',
      boxShadow: 'none',
      borderRadius: 2,
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: 'none',
        border: '1px solid rgba(var(--primary-color-rgb), 0.3)'
      }
    }}
  >
    <CardActionArea onClick={onSelect}>
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 1 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography 
            variant="subtitle2" 
            sx={{ 
              fontWeight: 600,
              color: '#2D3748'
            }}
          >
            {post.topic || 'Untitled Post'}
          </Typography>
            {post.type === 'carousel' && (
              <Chip
                size="small"
                label="Carousel"
                sx={{
                  bgcolor: 'rgba(var(--primary-color-rgb), 0.08)',
                  color: 'var(--primary-color)',
                  height: '20px'
                }}
              />
            )}
          </Box>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onMenuOpen(e, post);
            }}
            sx={{
              ml: 1,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>

        {post.image && (
          <Box sx={{ 
            mb: 1, 
            height: 100,
            borderRadius: 1,
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'rgba(0, 0, 0, 0.02)'
          }}>
            <img 
              src={post.image} 
              alt={post.topic || 'Post image'}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </Box>
        )}

        <Typography 
          variant="body2" 
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            whiteSpace: 'pre-wrap',
            fontSize: '0.875rem',
            lineHeight: 1.6,
            color: '#2D3748'
          }}
        >
          {fixSpecialCharacters(post.content)}
        </Typography>

        <Box sx={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 1.5
        }}>
          <Typography 
            variant="caption" 
            color="text.secondary"
            sx={{ fontSize: '0.75rem' }}
          >
            {formatPostDate(post.created_at)}
          </Typography>
          
          <Stack 
            direction="row" 
            spacing={0.5}
            onClick={e => e.stopPropagation()}
          >
            <Tooltip title="Copy">
              <IconButton 
                size="small" 
                onClick={(e) => onCopy(post.content, e)}
                color={copySuccess === post.content ? "success" : "default"}
                sx={{
                  width: 28,
                  height: 28,
                  '&:hover': {
                    backgroundColor: 'rgba(72, 144, 254, 0.08)'
                  },
                  color: copySuccess === post.content ? '#38A169' : '#718096'
                }}
              >
                {Boolean(copySuccess === post.content) ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Schedule">
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  onSchedule(post);
                }}
                sx={{
                  width: 28,
                  height: 28,
                  '&:hover': {
                    backgroundColor: 'rgba(72, 144, 254, 0.08)'
                  },
                  color: '#718096'
                }}
              >
                <CalendarIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Share to LinkedIn">
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  onLinkedIn(post);
                }}
                sx={{
                  width: 28,
                  height: 28,
                  '&:hover': {
                    backgroundColor: 'rgba(10, 102, 194, 0.08)'
                  },
                  color: '#0A66C2'
                }}
              >
                <LinkedInIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      </CardContent>
    </CardActionArea>
  </Card>
);

const Posts = () => {
  // Helper function to get current time plus 1 minute in HH:mm format
  const getCurrentTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 1);
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  };

  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [copySuccess, setCopySuccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuPost, setMenuPost] = useState(null);
  const [isSharing, setIsSharing] = useState(false);
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [openScheduleDialog, setOpenScheduleDialog] = useState(false);
  const [selectedTime, setSelectedTime] = useState(getCurrentTime());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [postToLinkedIn, setPostToLinkedIn] = useState(false);
  const [schedulingLoading, setSchedulingLoading] = useState(false);
  const [schedulingError, setSchedulingError] = useState('');
  const [linkedInConnected, setLinkedInConnected] = useState(false);

  // Reset time to current time when dialog opens
  const handleOpenScheduleDialog = (post) => {
    setSelectedPost(post);
    setSelectedTime(getCurrentTime());
    setOpenScheduleDialog(true);
  };

  // Add LinkedIn status check
  useEffect(() => {
    const checkLinkedInStatus = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/auth/linkedin-status`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        setLinkedInConnected(response.data.connected);
      } catch (error) {
        console.error('Error checking LinkedIn status:', error);
        setLinkedInConnected(false);
      }
    };

    if (user) {
      checkLinkedInStatus();
    }
  }, [user]);

  useEffect(() => {
    fetchPosts();
  }, []);

  useEffect(() => {
    if (posts.length > 0) {
      filterPosts();
    }
  }, [posts, searchTerm, filter]);

  const filterPosts = () => {
    let result = [...posts];
    
    // Apply search term filter
    if (searchTerm) {
      result = result.filter(post => 
        (post.topic && post.topic.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (post.content && post.content.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Apply type filter
    if (filter !== 'all') {
      if (filter === 'draft') {
        result = result.filter(post => !post.scheduled_date && !post.posted_to_linkedin);
      } else if (filter === 'scheduled') {
        result = result.filter(post => post.scheduled_date);
      } else if (filter === 'posted') {
        result = result.filter(post => post.posted_to_linkedin);
      }
    }
    
    setFilteredPosts(result);
  };

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const data = await postService.getPosts();
      
      console.log(`Fetched ${data.length} posts`);
      
      setPosts(data);
      setFilteredPosts(data);
    } catch (error) {
      console.error('Error fetching posts:', error);
      enqueueSnackbar('Failed to load posts', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, event) => {
    if (event) event.stopPropagation();
    try {
      await postService.deletePost(id);
      fetchPosts();
      enqueueSnackbar('Post deleted successfully', { variant: 'success' });
      
      // Close dialog if the deleted post is currently selected
      if (selectedPost && selectedPost.id === id) {
        setOpenDialog(false);
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      enqueueSnackbar('Failed to delete post', { variant: 'error' });
    }
  };

  const handleAddToCalendar = (post, event) => {
    if (event) event.stopPropagation();
    handleOpenScheduleDialog(post);
  };

  const handleCopy = async (content, event) => {
    if (event) event.stopPropagation();
    try {
      await navigator.clipboard.writeText(content);
      setCopySuccess(content);
      setTimeout(() => setCopySuccess(null), 2000);
      enqueueSnackbar('Content copied to clipboard', { variant: 'success' });
    } catch (err) {
      console.error('Failed to copy:', err);
      enqueueSnackbar('Failed to copy content', { variant: 'error' });
    }
  };

  const handleShareToLinkedIn = async (post) => {
    if (!post) return;
    
    try {
      setIsSharing(true);
      
      // Prepare the payload based on post type
      const payload = {
        text: post.content,
        image: null,
        attachment: null,
        postId: post.id // Add the post ID to identify which post to update
      };

      // If it's a carousel post, include the attachment directly
      if (post.type === 'carousel' && post.attachment) {
        payload.attachment = post.attachment; // Attachment is already base64 in the post object
        payload.title = "Like, Share & Repost"; // Add title for carousel posts
      } else if (post.image) {
        // If it's a regular post with image, include the image directly
        payload.image = post.image; // Image URL is already in the post object
      }
      
      console.log('Sharing post to LinkedIn:', {
        type: post.type,
        hasImage: !!post.image,
        hasAttachment: !!post.attachment
      });
      
      // Call LinkedIn share service
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/linkedin/share`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      console.log('LinkedIn share response:', response.data);
      
      // Refresh posts to update UI
      await fetchPosts();
      
      enqueueSnackbar('Successfully shared to LinkedIn!', { 
        variant: 'success',
        autoHideDuration: 4000
      });
      
    } catch (error) {
      console.error('LinkedIn share error:', error);
      
      enqueueSnackbar(error.response?.data?.message || 'Failed to share to LinkedIn', { 
        variant: 'error',
        autoHideDuration: 4000
      });
      
    } finally {
      setIsSharing(false);
    }
  };

  const handleMenuOpen = (event, post) => {
    event.stopPropagation();
    setMenuPost(post);
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuPost(null);
  };
  
  const handleEditPost = (post) => {
    handleMenuClose();
    // Navigate to edit post page or open edit dialog
    navigate('/dashboard/generate-post', { state: { editPost: post } });
  };

  const handleSchedulePost = async (post) => {
    try {
      setSchedulingLoading(true);
      setSchedulingError('');
      
      // Parse the selected time
      const [hours, minutes] = selectedTime.split(':').map(Number);
      
      // Create a date with the selected date and time
      const scheduledDate = new Date(selectedDate);
      scheduledDate.setHours(hours, minutes, 0, 0);

      // Check if the scheduled time is in the past
      const now = new Date();
      if (scheduledDate <= now) {
        setSchedulingError('Cannot schedule posts for past dates/times.');
        return;
      }
      
      // Format the date manually to ensure correct date and time
      const year = scheduledDate.getFullYear();
      const month = String(scheduledDate.getMonth() + 1).padStart(2, '0');
      const day = String(scheduledDate.getDate()).padStart(2, '0');
      const formattedHours = String(hours).padStart(2, '0');
      const formattedMinutes = String(minutes).padStart(2, '0');
      
      const formattedDate = `${year}-${month}-${day}T${formattedHours}:${formattedMinutes}:00.000Z`;
      
      // Schedule the post with the additional LinkedIn posting option
      await postService.schedulePost(post.id, formattedDate, postToLinkedIn);
      await fetchPosts();
      setOpenScheduleDialog(false);
      setSelectedTime(getCurrentTime());
      setSelectedDate(new Date());
      setPostToLinkedIn(false);
      
      // Reset time to current time when closing
      setSelectedTime(getCurrentTime());
      setSelectedDate(new Date());
      setPostToLinkedIn(false);
      
      // Show success notification
      enqueueSnackbar(
        postToLinkedIn 
          ? 'Post scheduled and will be shared to LinkedIn at the specified time!' 
          : 'Post scheduled successfully!', 
        { 
          variant: 'success',
          autoHideDuration: 4000
        }
      );
    } catch (error) {
      console.error('Error scheduling post:', error);
      setSchedulingError('Failed to schedule post. Please try again.');
      enqueueSnackbar('Failed to schedule post', { variant: 'error' });
    } finally {
      setSchedulingLoading(false);
    }
  };

  // Function to check if selected datetime is in the past
  const isDateTimeInPast = () => {
    if (!selectedDate || !selectedTime) return false;
    const [hours, minutes] = selectedTime.split(':').map(Number);
    const scheduledDate = new Date(selectedDate);
    scheduledDate.setHours(hours, minutes, 0, 0);
    return scheduledDate <= new Date();
  };

  if (loading) {
    return (
      <Box sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <AnimatedLogo size={100} color="#4890fe" />
        <Typography variant="h6" sx={{ mt: 3, color: '#2D3748', fontWeight: 600 }}>
          Loading posts...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This may take a few seconds
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>My LinkedIn Posts | Instation</title>
        <meta name="description" content="Manage and organize your LinkedIn posts with our powerful post management system." />
      </Helmet>
      
      <Container maxWidth="xl" className="page-container">
        <Box className="content-container">
          {/* Top Card */}
          <Paper elevation={0} className="content-paper" sx={{ p: 3, borderRadius: 2, border: '1px solid rgba(0, 0, 0, 0.08)', mb: 3 }}>
            {/* Header with search and create button */}
            <Box sx={{ 
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2
            }}>
              <Typography variant="h6" className="component-heading" sx={{ mb: 0 }}>
                My Posts
              </Typography>
              
              <Box sx={{ 
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap'
              }}>
                <TextField
                  placeholder="Search posts..."
                  size="small"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    sx: {
                      borderRadius: '8px',
                      bgcolor: 'background.paper'
                    }
                  }}
                  sx={{ width: { xs: '100%', sm: '220px' } }}
                />
                
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    color: 'var(--primary-color)',
                    borderColor: 'var(--primary-color)',
                    '&:hover': {
                      borderColor: 'var(--primary-dark)',
                      backgroundColor: 'rgba(var(--primary-color-rgb), 0.04)'
                    }
                  }}
                  onClick={() => navigate('/dashboard/generate-post')}
                >
                  Create New Post
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Columns Grid */}
          <Grid container spacing={3}>
            {/* Draft Column */}
            <Grid item xs={12} md={4}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 2,
                  height: 'calc(100vh - 200px)',
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: '#fff',
                  border: '1px solid rgba(var(--primary-color-rgb), 0.08)',
                  borderRadius: 2
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2,
                  p: 1
                }}>
                  <ArticleIcon sx={{ color: 'var(--primary-color)', mr: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'var(--primary-color)' }}>
                    Draft
                  </Typography>
                  <Chip 
                    label={filteredPosts.filter(post => !post.scheduled_date && !post.posted_to_linkedin).length}
                    size="small"
                    sx={{ 
                      ml: 'auto',
                      bgcolor: 'rgba(var(--primary-color-rgb), 0.08)',
                      color: 'var(--primary-color)'
                    }}
                  />
                </Box>
                <Box sx={{ 
                  flex: 1,
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                    width: '4px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#CBD5E0',
                    borderRadius: '4px',
                  },
                }}>
                  {filteredPosts
                    .filter(post => !post.scheduled_date && !post.posted_to_linkedin)
                    .map((post) => (
                      <PostCard 
                        key={post.id}
                        post={post}
                        onSelect={() => {
                          setSelectedPost(post);
                          setOpenDialog(true);
                        }}
                        onMenuOpen={handleMenuOpen}
                        onCopy={handleCopy}
                        onCalendar={handleAddToCalendar}
                        onLinkedIn={handleShareToLinkedIn}
                        copySuccess={copySuccess}
                        onSchedule={handleOpenScheduleDialog}
                      />
                    ))}
                </Box>
              </Paper>
            </Grid>

            {/* Scheduled Column */}
            <Grid item xs={12} md={4}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 2,
                  height: 'calc(100vh - 200px)',
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: '#fff',
                  border: '1px solid rgba(var(--primary-color-rgb), 0.08)',
                  borderRadius: 2
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2,
                  p: 1
                }}>
                  <CalendarIcon sx={{ color: 'var(--primary-color)', mr: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'var(--primary-color)' }}>
                    Scheduled
                  </Typography>
                  <Chip 
                    label={filteredPosts.filter(post => post.scheduled_date && !post.posted_to_linkedin).length}
                    size="small"
                    sx={{ 
                      ml: 'auto',
                      bgcolor: 'rgba(var(--primary-color-rgb), 0.08)',
                      color: 'var(--primary-color)'
                    }}
                  />
                </Box>
                <Box sx={{ 
                  flex: 1,
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                    width: '4px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#CBD5E0',
                    borderRadius: '4px',
                  },
                }}>
                  {filteredPosts
                    .filter(post => post.scheduled_date && !post.posted_to_linkedin)
                    .map((post) => (
                      <PostCard 
                        key={post.id}
                        post={post}
                        onSelect={() => {
                          setSelectedPost(post);
                          setOpenDialog(true);
                        }}
                        onMenuOpen={handleMenuOpen}
                        onCopy={handleCopy}
                        onCalendar={handleAddToCalendar}
                        onLinkedIn={handleShareToLinkedIn}
                        copySuccess={copySuccess}
                        onSchedule={handleOpenScheduleDialog}
                      />
                    ))}
                </Box>
              </Paper>
            </Grid>

            {/* Posted Column */}
            <Grid item xs={12} md={4}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 2,
                  height: 'calc(100vh - 200px)',
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: '#fff',
                  border: '1px solid rgba(var(--primary-color-rgb), 0.08)',
                  borderRadius: 2
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2,
                  p: 1
                }}>
                  <LinkedInIcon sx={{ color: 'var(--primary-color)', mr: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'var(--primary-color)' }}>
                    Posted
                  </Typography>
                  <Chip 
                    label={filteredPosts.filter(post => post.posted_to_linkedin).length}
                    size="small"
                    sx={{ 
                      ml: 'auto',
                      bgcolor: 'rgba(var(--primary-color-rgb), 0.08)',
                      color: 'var(--primary-color)'
                    }}
                  />
                </Box>
                <Box sx={{ 
                  flex: 1,
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                    width: '4px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#CBD5E0',
                    borderRadius: '4px',
                  },
                }}>
                  {filteredPosts
                    .filter(post => post.posted_to_linkedin)
                    .map((post) => (
                      <PostCard 
                        key={post.id}
                        post={post}
                        onSelect={() => {
                          setSelectedPost(post);
                          setOpenDialog(true);
                        }}
                        onMenuOpen={handleMenuOpen}
                        onCopy={handleCopy}
                        onCalendar={handleAddToCalendar}
                        onLinkedIn={handleShareToLinkedIn}
                        copySuccess={copySuccess}
                        onSchedule={handleOpenScheduleDialog}
                      />
                    ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* View Post Dialog */}
          <Dialog 
            open={openDialog} 
            onClose={() => setOpenDialog(false)}
            maxWidth="md"
            fullWidth
            sx={{
              '& .MuiDialog-paper': {
                margin: { xs: 2, sm: 4 },
                maxHeight: 'calc(100vh - 100px)',
                width: '95%',
                borderRadius: 2,
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(0, 0, 0, 0.05)',
                display: 'flex',
                flexDirection: 'column'
              }
            }}
          >
            <DialogTitle 
              sx={{ 
                fontSize: '1.25rem',
                fontWeight: 600,
                pb: 1,
                pt: 2.5,
                px: 3,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
              }}
            >
              <Box className="component-heading" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {selectedPost?.topic || 'Untitled Post'}
                {selectedPost?.type === 'carousel' && (
                  <Chip
                    label="Carousel"
                    size="small"
                    sx={{
                      bgcolor: 'rgba(var(--primary-color-rgb), 0.08)',
                      color: 'var(--primary-color)'
                    }}
                  />
                )}
                {Boolean(selectedPost?.post_to_linkedin) && (
                  <Chip
                    icon={<LinkedInIcon fontSize="small" />}
                    label={Boolean(selectedPost?.posted_to_linkedin) ? "Posted to LinkedIn" : "Scheduled for LinkedIn"}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(10, 102, 194, 0.08)',
                      color: '#0A66C2',
                      fontWeight: 500
                    }}
                  />
                )}
                {selectedPost?.scheduled_date && (
                  <Chip
                    icon={<CalendarIcon fontSize="small" />}
                    label={`Scheduled: ${new Date(selectedPost.scheduled_date).toLocaleDateString()}`}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(72, 144, 254, 0.08)',
                      color: '#4890fe',
                      fontWeight: 500
                    }}
                  />
                )}
              </Box>
              <IconButton 
                onClick={() => setOpenDialog(false)}
                size="small"
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>

            <DialogContent 
              sx={{ 
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden'
              }}
            >
              <Box 
                sx={{ 
                  flex: 1,
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                    width: '4px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#c1c1c1',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: '#a1a1a1',
                  },
                }}
              >
                {/* Text Content First */}
                <Typography 
                  variant="body1" 
                  sx={{ 
                    whiteSpace: 'pre-wrap',
                    fontSize: '0.95rem',
                    lineHeight: 1.6,
                    color: '#2D3748',
                    p: 1,
                    mb: 3
                  }}
                >
                  {fixSpecialCharacters(selectedPost?.content)}
                </Typography>

                {/* Carousel PDF */}
                {selectedPost?.type === 'carousel' && selectedPost?.attachment && (
                  <Box sx={{ 
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    <Box sx={{ 
                      width: '100%',
                      maxWidth: '600px',
                      height: '500px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
                    }}>
                      <iframe
                        src={`data:application/pdf;base64,${selectedPost.attachment}`}
                        width="100%"
                        height="100%"
                        style={{ border: 'none' }}
                        title="Carousel PDF"
                      />
                    </Box>
                  </Box>
                )}
                
                {/* Regular Image */}
                {selectedPost?.image && !selectedPost?.type === 'carousel' && (
                  <Box sx={{ 
                    textAlign: 'center',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                    maxHeight: '350px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    bgcolor: 'rgba(0, 0, 0, 0.02)'
                  }}>
                    <img 
                      src={selectedPost.image} 
                      alt={selectedPost.topic || "Post image"}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '350px',
                        objectFit: 'contain'
                      }}
                    />
                  </Box>
                )}
              </Box>
            </DialogContent>

            {/* Action buttons in a fixed position */}
            <Box sx={{ 
              p: 3, 
              borderTop: '1px solid rgba(0, 0, 0, 0.06)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1.5,
              bgcolor: 'background.paper'
            }}>
              {/* Left side - Delete button */}
              <Button
                variant="outlined"
                onClick={() => {
                  handleDelete(selectedPost?.id);
                  setOpenDialog(false);
                }}
                startIcon={<DeleteIcon />}
                size="medium"
                sx={{ 
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 500,
                  borderColor: 'rgba(239, 68, 68, 0.5)',
                  color: '#EF4444',
                  px: 2,
                  '&:hover': {
                    borderColor: '#EF4444',
                    backgroundColor: 'rgba(239, 68, 68, 0.04)'
                  }
                }}
              >
                Delete
              </Button>

              {/* Right side - Action buttons */}
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                {Boolean(selectedPost?.posted_to_linkedin) && Boolean(selectedPost?.linkedin_post_url) && (
                  <Button
                    variant="outlined"
                    onClick={() => window.open(selectedPost.linkedin_post_url, '_blank')}
                    startIcon={<LinkedInIcon />}
                    size="medium"
                    sx={{ 
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 500,
                      borderColor: 'rgba(10, 102, 194, 0.5)',
                      color: '#0A66C2',
                      px: 2,
                      '&:hover': {
                        borderColor: '#0A66C2',
                        backgroundColor: 'rgba(10, 102, 194, 0.04)'
                      }
                    }}
                  >
                    Open in LinkedIn
                  </Button>
                )}

                <Button
                  variant="outlined"
                  onClick={() => handleOpenScheduleDialog(selectedPost)}
                  startIcon={<CalendarIcon />}
                  size="medium"
                  sx={{ 
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 500,
                    borderColor: 'rgba(var(--primary-color-rgb), 0.5)',
                    color: 'var(--primary-color)',
                    px: 2,
                    '&:hover': {
                      borderColor: 'var(--primary-color)',
                      backgroundColor: 'rgba(var(--primary-color-rgb), 0.04)'
                    }
                  }}
                >
                  Schedule
                </Button>
                
                <Button 
                  variant="outlined" 
                  onClick={() => handleCopy(selectedPost?.content)}
                  color={copySuccess === selectedPost?.content ? "success" : "primary"}
                  startIcon={copySuccess === selectedPost?.content ? <CheckIcon /> : <CopyIcon />}
                  size="medium"
                  sx={{ 
                    textTransform: 'none',
                    fontWeight: 500,
                    borderRadius: '8px',
                    px: 2,
                    borderColor: copySuccess === selectedPost?.content ? 'rgba(56, 161, 105, 0.5)' : 'rgba(var(--primary-color-rgb), 0.5)',
                    color: copySuccess === selectedPost?.content ? '#38A169' : '#4890fe',
                    '&:hover': {
                      borderColor: copySuccess === selectedPost?.content ? '#38A169' : 'var(--primary-color)',
                      backgroundColor: copySuccess === selectedPost?.content ? 'rgba(56, 161, 105, 0.04)' : 'rgba(72, 144, 254, 0.04)'
                    }
                  }}
                >
                  {copySuccess === selectedPost?.content ? 'Copied' : 'Copy'}
                </Button>
                
                {!Boolean(selectedPost?.posted_to_linkedin) && (
                  <Button 
                    variant="contained" 
                    onClick={() => handleShareToLinkedIn(selectedPost)}
                    disabled={isSharing || !selectedPost}
                    startIcon={<LinkedInIcon />}
                    size="medium"
                    sx={{ 
                      textTransform: 'none',
                      fontWeight: 600,
                      bgcolor: '#0A66C2',
                      borderRadius: '8px',
                      px: 2.5,
                      boxShadow: '0 2px 6px rgba(10, 102, 194, 0.2)',
                      '&:hover': {
                        bgcolor: '#0959ab',
                        boxShadow: '0 4px 12px rgba(10, 102, 194, 0.3)'
                      },
                      '&.Mui-disabled': {
                        bgcolor: 'rgba(10, 102, 194, 0.12)',
                        color: 'rgba(10, 102, 194, 0.6)'
                      }
                    }}
                  >
                    {isSharing ? 'Sharing...' : 'Share to LinkedIn'}
                  </Button>
                )}
              </Box>
            </Box>
          </Dialog>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl) && !menuPost}
            onClose={() => setAnchorEl(null)}
            PaperProps={{
              sx: { 
                width: 220,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px'
              }
            }}
          >
            <MenuItem 
              onClick={() => {
                setFilter('all');
                setAnchorEl(null);
              }}
              selected={filter === 'all'}
            >
              All Posts
            </MenuItem>
            <Divider />
            <MenuItem 
              onClick={() => {
                setFilter('draft');
                setAnchorEl(null);
              }}
              selected={filter === 'draft'}
            >
              <ListItemIcon>
                <ArticleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Draft</ListItemText>
            </MenuItem>
            <MenuItem 
              onClick={() => {
                setFilter('scheduled');
                setAnchorEl(null);
              }}
              selected={filter === 'scheduled'}
            >
              <ListItemIcon>
                <CalendarIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Scheduled</ListItemText>
            </MenuItem>
            <MenuItem 
              onClick={() => {
                setFilter('posted');
                setAnchorEl(null);
              }}
              selected={filter === 'posted'}
            >
              <ListItemIcon>
                <LinkedInIcon fontSize="small" sx={{ color: '#0A66C2' }} />
              </ListItemIcon>
              <ListItemText>Posted</ListItemText>
            </MenuItem>
          </Menu>

          {/* Post Actions Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl) && Boolean(menuPost)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: { 
                width: 200,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px'
              }
            }}
          >
            {menuPost?.posted_to_linkedin && menuPost?.linkedin_post_url && (
              <MenuItem onClick={() => {
                window.open(menuPost.linkedin_post_url, '_blank');
                handleMenuClose();
              }}>
                <ListItemIcon>
                  <LinkedInIcon fontSize="small" sx={{ color: '#0A66C2' }} />
                </ListItemIcon>
                <ListItemText>Open in LinkedIn</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={() => {
              handleCopy(menuPost?.content);
              handleMenuClose();
            }}>
              <ListItemIcon>
                <CopyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Copy Content</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              handleAddToCalendar(menuPost);
              handleMenuClose();
            }}>
              <ListItemIcon>
                <CalendarIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Add to Calendar</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleEditPost(menuPost)}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit Post</ListItemText>
            </MenuItem>
            {user?.linkedin_access_token && (
              <MenuItem onClick={() => {
                handleShareToLinkedIn(menuPost);
                handleMenuClose();
              }}>
                <ListItemIcon>
                  <LinkedInIcon fontSize="small" sx={{ color: '#0A66C2' }} />
                </ListItemIcon>
                <ListItemText>Share to LinkedIn</ListItemText>
              </MenuItem>
            )}
            <Divider />
            <MenuItem onClick={() => {
              handleDelete(menuPost?.id);
              handleMenuClose();
            }} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Delete Post</ListItemText>
            </MenuItem>
          </Menu>

          {/* Schedule Post Dialog */}
          <Dialog 
            open={openScheduleDialog} 
            onClose={() => !schedulingLoading && setOpenScheduleDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              pb: 2
            }}>
              Schedule Post
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" className="section-title" sx={{ fontWeight: 500, color: 'black', mb: 1 }}>
                  Schedule Date
                </Typography>
                <TextField
                  type="date"
                  value={selectedDate.toISOString().split('T')[0]}
                  onChange={(e) => setSelectedDate(new Date(e.target.value))}
                  fullWidth
                  disabled={schedulingLoading}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: new Date().toISOString().split('T')[0]
                  }}
                  sx={{ mb: 3 }}
                />

                <Typography variant="subtitle2" className="section-title" sx={{ fontWeight: 500, color: 'black', mb: 1 }}>
                  Schedule Time
                </Typography>
                <TextField
                  type="time"
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(e.target.value)}
                  fullWidth
                  disabled={schedulingLoading}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    step: 300, // 5 min
                  }}
                />
              </Box>
              
              <Divider sx={{ my: 3 }} />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={postToLinkedIn}
                    onChange={(e) => setPostToLinkedIn(e.target.checked)}
                    color="primary"
                    disabled={schedulingLoading || !linkedInConnected}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LinkedInIcon sx={{ mr: 1, color: '#0A66C2' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Automatically post to LinkedIn at scheduled time
                    </Typography>
                  </Box>
                }
              />
              
              {!linkedInConnected && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Connect your LinkedIn account in your profile settings to enable automatic posting.
                </Alert>
              )}

              {schedulingError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {schedulingError}
                </Alert>
              )}

              {isDateTimeInPast() && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Selected date and time is in the past. Please choose a future date/time.
                </Alert>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button 
                onClick={() => setOpenScheduleDialog(false)} 
                disabled={schedulingLoading}
              >
                Cancel
              </Button>
              <Button 
                onClick={() => handleSchedulePost(selectedPost)} 
                variant="contained" 
                disabled={schedulingLoading || isDateTimeInPast()}
              >
                {schedulingLoading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Scheduling...
                  </>
                ) : (
                  'Schedule Post'
                )}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Container>
    </>
  );
};

export default Posts; 